import { useState } from "react";

import { InfoIcon } from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import { useHistory } from "react-router";

import { Banner, Button, Icon, Text, toastError } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import {
  useGetIsLegalEntity,
  useGetOwners,
  useMutationRequest,
} from "~/v2/api/hooks";
import { enrollInFPSL, patchEsign } from "~/v2/api/rest/account";
import { postAgreementForLegalEntityAndSigner } from "~/v2/api/rest/entityOnboarding";
import { Path } from "~/v2/path";
import {
  CONSENT_TO_COLLATERAL_AGREEMENT_URL,
  MSLA_AGREEMENT_REVISION,
  MSLA_AGREEMENT_URL,
  RISK_DISCLOSURES_SECURITIES_LENDING_URL,
} from "../entityOnboarding/constants";
import type { FPSLOnboardingProps } from "./index";

export const FpslAgreement = ({
  currentAccount,
  onBack,
}: FPSLOnboardingProps) => {
  const history = useHistory();
  const [isAgreementViewed, setIsAgreementViewed] = useState(false);

  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();

  const fpslEnrollmentMutation = useMutationRequest(
    () => enrollInFPSL(currentAccount.id),
    {
      onSuccess: () => {
        AmplitudeProvider.dispatch("algodash_enrolled_fpsl");
        history.push(Path.ROUTE_DASHBOARD, { showFpslSuccessModal: true });
      },
    },
  );

  const { owners } = useGetOwners({
    accountId: currentAccount?.id ?? "",
    options: {
      enabled: !!currentAccount?.id && isLegalEntity && !isLegalEntityLoading,
    },
  });

  const legalEntityOwner = owners?.find((details) => details.is_legal_entity);

  const accountOpener = owners?.find(
    (details) => details.is_legal_account_opener,
  );

  const mslaAgreementMutation = useMutationRequest(
    async () =>
      isLegalEntity && accountOpener
        ? postAgreementForLegalEntityAndSigner({
            ownerId: legalEntityOwner?.owner_id ?? "",
            agreement: "master_securities_lending_agreement",
            accountId: currentAccount?.id ?? "",
            signerPartyId: accountOpener?.owner_id ?? "",
            revision: MSLA_AGREEMENT_REVISION,
          })
        : patchEsign(currentAccount?.id ?? "", {
            agreement: "master_securities_lending_agreement",
            revision: MSLA_AGREEMENT_REVISION,
            signed_at: new Date(),
          }),
    {
      onError: (error) => {
        Sentry.captureException(error);
        toastError("Failed to sign Master Securities Lending Agreement");
      },
    },
  );

  const onViewAgreementClicked = () => {
    window.open(MSLA_AGREEMENT_URL, "_blank", "noopener");
    setIsAgreementViewed(true);
  };

  const onSubmit = async () => {
    await mslaAgreementMutation.mutateAsync(undefined);
    await fpslEnrollmentMutation.mutateAsync(undefined);
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="space-y-4 rounded-lg border border-gray-200 p-4">
        <Text className="font-bold text-gray-800">
          Master Securities Lending Agreement for Fully Paid Securities Lending
        </Text>
        <Banner
          variant="info"
          message="Please view the agreement to accept the terms."
          className="rounded"
          icon={<InfoIcon size={20} className="text-indigo-600" />}
        />
      </div>

      <Button
        variant="v2/stroke"
        onClick={onViewAgreementClicked}
        className="inline-flex items-center gap-2"
      >
        View Agreement
        <Icon name="ArrowTopRightOnSquare" />
      </Button>

      <div className="flex items-start gap-3">
        <label
          htmlFor="agreement-checkbox"
          className="text-warm-gray-500 text-xs"
        >
          I have read, understood, and agree to be bound by the{" "}
          <a
            href={MSLA_AGREEMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline hover:text-blue-800"
          >
            Alpaca Master Securities Lending Agreement
          </a>
          , the{" "}
          <a
            href={CONSENT_TO_COLLATERAL_AGREEMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline hover:text-blue-800"
          >
            Consent to Collateral Administration Agreement
          </a>
          , and{" "}
          <a
            href={RISK_DISCLOSURES_SECURITIES_LENDING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline hover:text-blue-800"
          >
            Risk Disclosures
          </a>{" "}
          With Respect to Participating in Stock Lending ("Fully Paid Securities
          Lending").
        </label>
      </div>

      <div className="mt-4 flex justify-between">
        <Button variant="v2/stroke" onClick={onBack}>
          Back
        </Button>
        <Button
          variant="v2/filled"
          onClick={onSubmit}
          disabled={!isAgreementViewed || fpslEnrollmentMutation.isPending}
          loading={fpslEnrollmentMutation.isPending}
        >
          I Agree
        </Button>
      </div>
    </div>
  );
};
